/**
 * Children module — "Deti": the HIGH-VALUE destination. A family-wide grid of
 * kid cards that each open a kid-profile view — "a child's whole life in one
 * safe place". From a profile, every per-child surface (health, contacts,
 * calendar, documents) is one tap away, pre-filtered to that child.
 *
 * The framing is deliberately inclusive: this is the shared, scope-permissioned
 * place both parents AND grandparents reach for. Nothing here is co-parenting
 * specific — it reads identically in 'family' and 'separated' mode.
 *
 * Navigation into a scoped module works by setting the active child (the same
 * selection the ChildrenSwitcher drives) and routing to `/m/<slug>`. The target
 * module reads `activeChild` from the family context, so it lands pre-filtered.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ageFromBirthDate,
  canAccess,
  type Child,
  type Contact,
  type CalendarEvent,
  type Document as SolandiaDocument,
  type HealthRecord,
} from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { EmptyState, SectionHeader, Spinner } from '../../components/ui';
import { AddChildDialog } from './AddChildDialog';

/**
 * Soft per-child tint, picked deterministically from the child id so a kid keeps
 * the same accent colour everywhere. Pure presentation — evolves the palette per
 * the design system (per-child pastel tints) without new CSS.
 */
const CHILD_TINTS = [
  'rgba(79, 109, 245, 0.12)', // indigo
  'rgba(245, 166, 35, 0.16)', // sun-amber
  'rgba(46, 196, 152, 0.14)', // mint
  'rgba(236, 110, 173, 0.14)', // pink
  'rgba(124, 92, 240, 0.14)', // violet
  'rgba(90, 178, 255, 0.14)', // sky
] as const;

function tintFor(childId: string): string {
  let hash = 0;
  for (let i = 0; i < childId.length; i += 1) {
    hash = (hash * 31 + childId.charCodeAt(i)) >>> 0;
  }
  return CHILD_TINTS[hash % CHILD_TINTS.length]!;
}

/** Localised "DD. MM." day/month, or null when no birth date is on file. */
function formatBirthday(birthDate: string): string | null {
  if (!birthDate) return null;
  const d = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('sk-SK', { day: 'numeric', month: 'long' });
}

export function ChildrenModule() {
  const { t } = useI18n();
  const { children, childrenLoading, membership, setActiveChild } = useFamily();
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const canManageChildren = canAccess(membership, 'children');
  const selected = children.find((c) => c.id === selectedId) ?? null;

  /** Open a per-child module pre-filtered to `child`. */
  function openModule(child: Child, slug: string) {
    setActiveChild(child.id);
    navigate(`/m/${slug}`);
  }

  if (childrenLoading) {
    return (
      <section>
        <SectionHeader title={t.children.title} />
        <Spinner />
      </section>
    );
  }

  // Detail view — one child's whole life in one place.
  if (selected) {
    return (
      <KidProfile
        child={selected}
        canManageChildren={canManageChildren}
        onBack={() => setSelectedId(null)}
        onOpenModule={openModule}
        onRemoved={() => setSelectedId(null)}
      />
    );
  }

  // Grid view — the family's kids at a glance.
  return (
    <section>
      <SectionHeader
        title={t.children.title}
        subtitle={t.children.profileLead}
        action={
          canManageChildren && children.length > 0 ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setAdding(true)}
            >
              ＋ {t.children.add}
            </button>
          ) : null
        }
      />

      {children.length === 0 ? (
        <EmptyState
          emoji="👶"
          title={t.children.none}
          hint={t.children.noneLead}
          action={
            canManageChildren ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setAdding(true)}
              >
                ＋ {t.children.add}
              </button>
            ) : null
          }
        />
      ) : (
        <ul
          className="kid-grid"
          style={{
            display: 'grid',
            gap: 'var(--sol-space-md)',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {children.map((c) => (
            <li key={c.id}>
              <KidCard child={c} onOpen={() => setSelectedId(c.id)} />
            </li>
          ))}
          {canManageChildren ? (
            <li>
              <button
                type="button"
                className="card"
                onClick={() => setAdding(true)}
                style={{
                  width: '100%',
                  height: '100%',
                  minHeight: 132,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  cursor: 'pointer',
                  borderStyle: 'dashed',
                  color: 'var(--sol-muted)',
                  fontWeight: 600,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: '1.6rem' }}>
                  ＋
                </span>
                {t.children.add}
              </button>
            </li>
          ) : null}
        </ul>
      )}

      {adding ? (
        <AddChildDialog
          onClose={() => setAdding(false)}
          onCreated={() => setAdding(false)}
        />
      ) : null}
    </section>
  );
}

function KidCard({ child, onOpen }: { child: Child; onOpen: () => void }) {
  const { t } = useI18n();
  const age = ageFromBirthDate(child.birthDate, new Date());
  return (
    <button
      type="button"
      className="card"
      onClick={onOpen}
      aria-label={`${child.name} — ${t.children.profileTitle}`}
      style={{
        width: '100%',
        height: '100%',
        minHeight: 132,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 10,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: 'var(--sol-radius-pill)',
          background: tintFor(child.id),
          fontSize: '1.9rem',
        }}
      >
        {child.avatarEmoji}
      </span>
      <div className="stack-sm" style={{ gap: 2 }}>
        <strong style={{ fontSize: '1.05rem' }}>{child.name}</strong>
        <span className="text-sm muted">
          {age} {t.children.ageYears}
        </span>
      </div>
    </button>
  );
}

function KidProfile({
  child,
  canManageChildren,
  onBack,
  onOpenModule,
  onRemoved,
}: {
  child: Child;
  canManageChildren: boolean;
  onBack: () => void;
  onOpenModule: (child: Child, slug: string) => void;
  onRemoved: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const age = ageFromBirthDate(child.birthDate, new Date());
  const birthday = formatBirthday(child.birthDate);
  const [removing, setRemoving] = useState(false);

  async function remove() {
    if (!window.confirm(t.children.removeConfirm)) return;
    setRemoving(true);
    try {
      await api.deleteChild(family.id, child.id);
      onRemoved();
    } catch {
      setRemoving(false);
    }
  }

  return (
    <section>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onBack}
        style={{ marginBottom: 12 }}
      >
        ← {t.children.title}
      </button>

      {/* Profile header — who this child is, at a glance. */}
      <div
        className="card"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sol-space-md)',
          flexWrap: 'wrap',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: 'var(--sol-radius-pill)',
            background: tintFor(child.id),
            fontSize: '2.2rem',
            flex: 'none',
          }}
        >
          {child.avatarEmoji}
        </span>
        <div className="grow stack-sm" style={{ gap: 2 }}>
          <h2 className="section-title" style={{ margin: 0 }}>
            {child.name}
          </h2>
          <span className="text-sm muted">
            {t.children.ageLabel}: {age} {t.children.ageYears}
            {birthday ? ` · ${t.children.birthday}: ${birthday}` : ''}
          </span>
          {!birthday ? (
            <span className="text-xs muted">{t.children.noBirthDate}</span>
          ) : null}
        </div>
        {canManageChildren ? (
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={remove}
            disabled={removing}
            style={{ flex: 'none' }}
            aria-label={`${t.common.delete} ${child.name}`}
          >
            {removing ? t.common.saving : t.common.delete}
          </button>
        ) : null}
      </div>

      {/* Grandparent-accessible framing — the quiet value line. */}
      <p className="lead" style={{ margin: '16px 0 12px' }}>
        {t.children.profileLead}
      </p>

      {/* Quick links — one tap into each scoped module, pre-filtered. */}
      <div
        style={{
          display: 'grid',
          gap: 'var(--sol-space-md)',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        }}
      >
        <HealthQuickLink child={child} onOpen={() => onOpenModule(child, 'health')} />
        <ContactsQuickLink child={child} onOpen={() => onOpenModule(child, 'contacts')} />
        <CalendarQuickLink child={child} onOpen={() => onOpenModule(child, 'calendar')} />
        <DocumentsQuickLink child={child} onOpen={() => onOpenModule(child, 'documents')} />
      </div>
    </section>
  );
}

/**
 * A quick-link card: an icon, a label, a one-line live summary, and a one-tap
 * jump into the scoped module. `summary` is rendered from the per-child data the
 * underlying module loads, so the profile is a real index, not just buttons.
 */
function QuickLink({
  icon,
  label,
  onOpen,
  children,
}: {
  icon: string;
  label: string;
  onOpen: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="card"
      onClick={onOpen}
      aria-label={label}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sol-space-sm)',
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: '1.5rem', lineHeight: 1.1 }}>
        {icon}
      </span>
      <span className="grow stack-sm" style={{ gap: 2 }}>
        <strong>{label}</strong>
        <span className="text-sm muted">{children}</span>
      </span>
      <span aria-hidden="true" className="muted">
        →
      </span>
    </button>
  );
}

function HealthQuickLink({
  child,
  onOpen,
}: {
  child: Child;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const { data } = useAsync<HealthRecord | null>(
    () => api.getHealthRecord(family.id, child.id).catch(() => null),
    [family.id, child.id],
  );

  let summary: string = t.common.loading;
  if (data !== undefined) {
    const allergies = data?.allergies?.trim();
    const insurance = data?.insurance?.trim();
    if (allergies) summary = `${t.health.allergies}: ${allergies}`;
    else if (insurance) summary = `${t.health.insurance}: ${insurance}`;
    else summary = t.health.placeholderNone;
  }

  return (
    <QuickLink icon="🩺" label={t.children.quickHealth} onOpen={onOpen}>
      {summary}
    </QuickLink>
  );
}

function ContactsQuickLink({
  child,
  onOpen,
}: {
  child: Child;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const { data } = useAsync<Contact[]>(async () => {
    const res = await api.listContacts(family.id, child.id);
    return res.items;
  }, [family.id, child.id]);

  const count = data?.length ?? null;
  const doctor = data?.find((c) => c.type === 'doctor');
  let summary: string = t.common.loading;
  if (count !== null) {
    if (count === 0) summary = t.contacts.empty;
    else if (doctor) summary = `${t.contacts.types.doctor}: ${doctor.label}`;
    else summary = `${count} ${t.children.quickContacts.toLowerCase()}`;
  }

  return (
    <QuickLink icon="📇" label={t.children.quickContacts} onOpen={onOpen}>
      {summary}
    </QuickLink>
  );
}

function CalendarQuickLink({
  child,
  onOpen,
}: {
  child: Child;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const { data } = useAsync<CalendarEvent[]>(async () => {
    const res = await api.listEvents(family.id, child.id);
    return res.items;
  }, [family.id, child.id]);

  let summary: string = t.common.loading;
  if (data !== undefined) {
    const now = Date.now();
    const next = [...data]
      .filter((e) => new Date(e.startAt).getTime() >= now)
      .sort((a, b) => a.startAt.localeCompare(b.startAt))[0];
    if (next) {
      const when = new Date(next.startAt).toLocaleDateString('sk-SK', {
        day: 'numeric',
        month: 'short',
      });
      summary = `${when} · ${next.title}`;
    } else {
      summary = t.calendar.empty;
    }
  }

  return (
    <QuickLink icon="📅" label={t.children.quickCalendar} onOpen={onOpen}>
      {summary}
    </QuickLink>
  );
}

function DocumentsQuickLink({
  child,
  onOpen,
}: {
  child: Child;
  onOpen: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family } = useFamily();
  const { data } = useAsync<SolandiaDocument[]>(async () => {
    const res = await api.listDocuments(family.id, child.id);
    return res.items;
  }, [family.id, child.id]);

  const count = data?.length ?? null;
  let summary: string = t.common.loading;
  if (count !== null) {
    summary =
      count === 0
        ? t.documents.empty
        : `${count} ${t.children.quickDocuments.toLowerCase()}`;
  }

  return (
    <QuickLink icon="📄" label={t.children.quickDocuments} onOpen={onOpen}>
      {summary}
    </QuickLink>
  );
}
